import React, { useState, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Package, ChevronRight, Plus, Loader2, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { GiroProductRow, GiroProductRowReadOnly } from "./GiroProductRow";

function InlineCatalogRow({ product, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQty(1);
      // small delay so the input is mounted before focus
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const confirm = () => {
    const q = Math.max(0, Number(qty) || 0);
    if (q <= 0) return;
    onConfirm?.(product, q);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`inline-catalog-add-${product.sku}`}
        className="w-full text-left px-2 py-2 border-b border-white/5 flex items-center gap-2 hover:bg-blue-500/5 transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0">
          <Plus className="w-4 h-4 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-zinc-200 text-xs truncate">{product.name}</p>
          <p className="text-zinc-500 text-[10px] truncate">
            {product.sku ? <span className="font-mono">{product.sku}</span> : null}
            {product.category_name && <span className="ml-1.5">· {product.category_name}</span>}
          </p>
        </div>
        <span className="text-zinc-400 font-mono text-xs shrink-0">
          {Number(product.price_list || 0).toFixed(2)}
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid={`inline-catalog-confirm-${product.sku}`}
      className="px-2 py-2 border-b border-white/5 bg-blue-500/10 ring-1 ring-blue-500/30 rounded-md"
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-zinc-100 text-xs truncate font-medium">{product.name}</p>
          <p className="text-zinc-400 text-[10px] truncate font-mono">{product.sku}</p>
        </div>
        <span className="text-zinc-300 font-mono text-xs shrink-0">
          €{Number(product.price_list || 0).toFixed(2)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <label className="text-[10px] text-zinc-400 shrink-0">Q.tà:</label>
        <Input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") confirm(); }}
          className="h-9 w-20 bg-[#09090b] border-white/10 text-white text-sm text-center font-mono"
          data-testid={`inline-catalog-qty-${product.sku}`}
        />
        <button
          type="button"
          onClick={confirm}
          data-testid={`inline-catalog-confirm-btn-${product.sku}`}
          className="flex-1 h-9 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
        >
          <Check className="w-4 h-4" /> Aggiungi
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid={`inline-catalog-cancel-${product.sku}`}
          className="h-9 w-9 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center shrink-0"
          aria-label="Annulla"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function StopProductsList({
  lockedOrder,
  contrattualizzato = false,
  orderItems,
  stopProducts,
  extraProducts,
  filteredMain,
  filteredOther,
  showOther,
  setShowOther,
  showNoteFor,
  setShowNoteFor,
  showDiscountFor,
  setShowDiscountFor,
  scontoCanale,
  clientDiscounts,
  recentOmaggi = {},
  onToggle,
  onQtyChange,
  onOmChange,
  onNoteChange,
  onDiscountChange,
  inlineCatalogResults = [],
  inlineCatalogSearching = false,
  onAddCatalogProduct,
  inlineSearchActive = false,
  lastActivatedCod = null,
  onActivationConsumed = () => {},
}) {
  if (lockedOrder) {
    if (Object.keys(orderItems).length === 0) {
      return (
        <div className="text-center py-8">
          <Package className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">Nessun articolo nell'ordine</p>
        </div>
      );
    }
    return (
      <>
        {[...stopProducts, ...extraProducts]
          .filter(p => orderItems[p.cod_articolo] !== undefined)
          .map(p => (
            <GiroProductRowReadOnly
              key={p.cod_articolo}
              product={p}
              entry={orderItems[p.cod_articolo]}
            />
          ))}
      </>
    );
  }

  // Hide products marked as non_disponibile (out-of-catalog / discontinued)
  const visibleMain = filteredMain.filter(p => !p.non_disponibile);
  const visibleOther = filteredOther.filter(p => !p.non_disponibile);
  const visibleCatalog = (inlineCatalogResults || []).filter(p => !p.non_disponibile);

  // When the user is actively searching, show ONE flat list (no category headers,
  // no "Altri articoli comuni" toggle, no "DA CATALOGO" header). Outside the
  // search, keep the original 3-tier layout (recent → category headers, then
  // collapsible "Altri articoli comuni", then catalog).
  if (inlineSearchActive) {
    const flatClient = [...visibleMain, ...visibleOther];
    const empty = flatClient.length === 0 && visibleCatalog.length === 0 && !inlineCatalogSearching;
    return (
      <>
        {flatClient.map(p => (
          <GiroProductRow
            key={p.cod_articolo}
            product={p}
            entry={orderItems[p.cod_articolo]}
            noteOpen={showNoteFor === p.cod_articolo}
            discountOpen={showDiscountFor === p.cod_articolo}
            scontoCanale={scontoCanale}
            clientDiscount={clientDiscounts[p.cod_articolo]}
            recentOmaggio={recentOmaggi[p.cod_articolo]}
            showDate={true}
            contrattualizzato={contrattualizzato}
            onToggle={onToggle}
            onNoteButtonClick={(cod) => setShowNoteFor(showNoteFor === cod ? null : cod)}
            onDiscountButtonClick={(cod) => setShowDiscountFor(showDiscountFor === cod ? null : cod)}
            onQtyChange={onQtyChange}
            onOmChange={onOmChange}
            onNoteChange={onNoteChange}
            onDiscountChange={onDiscountChange}
            justActivated={lastActivatedCod === p.cod_articolo}
            onActivationConsumed={onActivationConsumed}
          />
        ))}
        {visibleCatalog.map(p => (
          <InlineCatalogRow
            key={p.id}
            product={p}
            onConfirm={(prod, qty) => onAddCatalogProduct?.(prod, qty)}
          />
        ))}
        {inlineCatalogSearching && (
          <div className="flex items-center justify-center gap-2 py-3 text-blue-400 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Ricerca catalogo…
          </div>
        )}
        {empty && (
          <div className="text-center py-8">
            <Package className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">Nessun prodotto trovato</p>
          </div>
        )}
      </>
    );
  }

  const rendered = [];
  // "Selezionati" section: gather all currently-selected items that are NEW for the client,
  // i.e. articles added from catalog search ("+") OR stop articles WITHOUT past order history.
  // Articles already ordered in the past (with ultima_data) stay in their category even when selected.
  const isHistorical = (p) => (p.ultima_qta || 0) > 0 || (p.ultimo_reso_qta || 0) > 0;
  const selectedItems = visibleMain.filter(p => orderItems[p.cod_articolo] !== undefined && (p._extra || !isHistorical(p)));
  const restItems = visibleMain.filter(p => !(orderItems[p.cod_articolo] !== undefined && (p._extra || !isHistorical(p))));
  if (selectedItems.length > 0) {
    rendered.push(
      <div
        key="cat-selected"
        data-testid="category-header-selected"
        className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 border-b border-emerald-500/30 flex items-center gap-1.5"
      >
        <Check className="w-3 h-3" />
        Selezionati ({selectedItems.length})
      </div>
    );
    for (const p of selectedItems) {
      rendered.push(
        <GiroProductRow
          key={`sel-${p.cod_articolo}`}
          product={p}
          entry={orderItems[p.cod_articolo]}
          noteOpen={showNoteFor === p.cod_articolo}
          discountOpen={showDiscountFor === p.cod_articolo}
          scontoCanale={scontoCanale}
          clientDiscount={clientDiscounts[p.cod_articolo]}
          recentOmaggio={recentOmaggi[p.cod_articolo]}
          showDate={true}
          contrattualizzato={contrattualizzato}
          onToggle={onToggle}
          onNoteButtonClick={(cod) => setShowNoteFor(showNoteFor === cod ? null : cod)}
          onDiscountButtonClick={(cod) => setShowDiscountFor(showDiscountFor === cod ? null : cod)}
          onQtyChange={onQtyChange}
          onOmChange={onOmChange}
          onNoteChange={onNoteChange}
          onDiscountChange={onDiscountChange}
          justActivated={lastActivatedCod === p.cod_articolo}
          onActivationConsumed={onActivationConsumed}
        />
      );
    }
  }
  let currentCatCode = null;
  for (const p of restItems) {
    const catCode = p.category_code || "__none__";
    const catLabel = p.category_name || "Senza categoria";
    if (catCode !== currentCatCode) {
      currentCatCode = catCode;
      rendered.push(
        <div
          key={`cat-${catCode}`}
          data-testid={`category-header-${catCode}`}
          className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 border-b border-white/5"
        >
          {catLabel}
        </div>
      );
    }
    rendered.push(
      <GiroProductRow
        key={p.cod_articolo}
        product={p}
        entry={orderItems[p.cod_articolo]}
        noteOpen={showNoteFor === p.cod_articolo}
        discountOpen={showDiscountFor === p.cod_articolo}
        scontoCanale={scontoCanale}
        clientDiscount={clientDiscounts[p.cod_articolo]}
        recentOmaggio={recentOmaggi[p.cod_articolo]}
        showDate={true}
        contrattualizzato={contrattualizzato}
        onToggle={onToggle}
        onNoteButtonClick={(cod) => setShowNoteFor(showNoteFor === cod ? null : cod)}
        onDiscountButtonClick={(cod) => setShowDiscountFor(showDiscountFor === cod ? null : cod)}
        onQtyChange={onQtyChange}
        onOmChange={onOmChange}
        onNoteChange={onNoteChange}
        onDiscountChange={onDiscountChange}
        justActivated={lastActivatedCod === p.cod_articolo}
        onActivationConsumed={onActivationConsumed}
      />
    );
  }

  return (
    <>
      {rendered}

      {visibleMain.length === 0 && visibleOther.length === 0 && (
        <div className="text-center py-8">
          <Package className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">Nessun prodotto</p>
        </div>
      )}

      {visibleOther.length > 0 && (
        <button
          onClick={() => setShowOther(v => !v)}
          data-testid="toggle-other-products"
          className="w-full mt-3 p-3 rounded-lg border border-dashed border-white/10 hover:border-amber-500/30 bg-[#0f0f11] hover:bg-[#18181b] text-left transition-colors flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Package className="w-4 h-4 text-zinc-400 shrink-0" />
            <span className="text-zinc-300 text-xs font-medium">Altri articoli comuni</span>
            <Badge className="bg-white/5 text-zinc-400 text-[10px] shrink-0">{visibleOther.length}</Badge>
          </div>
          <ChevronRight className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${showOther ? "rotate-90" : ""}`} />
        </button>
      )}

      {showOther && visibleOther.map(p => (
        <GiroProductRow
          key={p.cod_articolo}
          product={p}
          entry={orderItems[p.cod_articolo]}
          noteOpen={showNoteFor === p.cod_articolo}
          discountOpen={showDiscountFor === p.cod_articolo}
          scontoCanale={scontoCanale}
          clientDiscount={clientDiscounts[p.cod_articolo]}
          recentOmaggio={recentOmaggi[p.cod_articolo]}
          showDate={false}
          contrattualizzato={contrattualizzato}
          onToggle={onToggle}
          onNoteButtonClick={(cod) => setShowNoteFor(showNoteFor === cod ? null : cod)}
          onDiscountButtonClick={(cod) => setShowDiscountFor(showDiscountFor === cod ? null : cod)}
          onQtyChange={onQtyChange}
          onOmChange={onOmChange}
          onNoteChange={onNoteChange}
          onDiscountChange={onDiscountChange}
          justActivated={lastActivatedCod === p.cod_articolo}
          onActivationConsumed={onActivationConsumed}
        />
      ))}
    </>
  );
}
